import React, { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import useData from 'hooks/useData';
import { formatDate } from 'utils/date';
import type { DummyCoworker } from 'types/scenario';
import InlineMessage from '@ingka/inline-message';
import ListView, { ListViewItem } from '@ingka/list-view';
import warningTriangle from '@ingka/ssr-icon/paths/warning-triangle';
import ProfilePicture from 'components/ProfilePicture';
import { getCostCentreDescription } from 'hooks/useGrid/gridFunctions';
import { useTranslation } from 'react-i18next';
import { getShortCC } from 'utils/text';
import contractTypes from 'providers/staticContractTypes';
import { API } from 'services';
import type { Coworker } from 'types/coworker';
import useAuth from 'hooks/useAuth';
import Text from '@ingka/text';
import useApp from 'hooks/useApp';
import { isNullOrUndefined } from 'utils/validation';
import type { TFunction } from 'i18next';
import CoworkerContribution from './CoworkerContribution';
import CoworkerCostCentre from './CoworkerCostCentre';
import { getContractHoursRange, getCoworkerCostCentres } from './helpers';
import classes from './CoworkerDetails.module.scss';
import { CoworkerDetailsEditModal } from './CoworkerDetailsEditModal';
import CoworkerData from './CoworkerData';
import CoworkerFutureData from './CoworkerFutureData';

export const DeactivatedCoworker = ({ t }: { t: TFunction<'translation', undefined> }) => (
    <InlineMessage
        data-testid="coworker-details-deactivated"
        className={classes['deactivated-coworker']}
        ssrIcon={warningTriangle}
        body={t('DEACTIVATED_COWORKER_WARNING')}
        variant="cautionary"
        subtle
    />
);

const CoworkerDetails = ({
    coworker,
    isActive,
    editCoworkerSheet,
    handleEditCoworkerSheet
}: {
    coworker: Coworker | DummyCoworker;
    isActive: boolean;
    editCoworkerSheet: boolean,
    handleEditCoworkerSheet: Dispatch<SetStateAction<boolean>>
}) => {
    const { t } = useTranslation();
    const [imgSrc, setImgSrc] = useState<string | null>();
    const { access } = useAuth();
    const { orgSelection: { utils: { hierarchy } } } = useApp();
    const { currentCountry, currentUnit, currentScenario } = useData();
    const costCentres = getCoworkerCostCentres(coworker, hierarchy?.costCentreHierarchy);

    useEffect(() => {
        if (currentCountry && currentUnit) {
            API().getUserProfilePic((coworker as Coworker).email)
                .then(objectUrl => {
                    setImgSrc(objectUrl);
                })
                .catch(() => {
                    setImgSrc(null);
                });
        }

        // clean up
        return () => {
            if (imgSrc) { URL.revokeObjectURL(imgSrc); }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coworker]);

    const coworkerDelta = currentScenario?.scenarioData.find(({ personId }) => personId === coworker.personId);
    if (!isNullOrUndefined(coworkerDelta)) {
        coworkerDelta.contractDeltas.forEach(delta => {
            delta.costDistributions?.forEach(deltaCd => {
                if (!costCentres.some(originalCc => originalCc.costCentre === deltaCd.costCentre)) {
                    costCentres.push({
                        costCentre: deltaCd.costCentre,
                        displayName: `${getShortCC(deltaCd.costCentre)} ${getCostCentreDescription(
                            deltaCd.costCentre,
                            hierarchy?.costCentreHierarchy,
                        )}`,
                        sourceIsPa: false,
                    });
                }
            });
        });
    }

    const portalDiv = document.getElementById('portal');

    return (
        <div className={classes['coworker-details-container']} data-testid="coworker-details">
            <div className={classes['profile-container-bg']} data-testid="coworker-details-profile">
                <div className={classes['profile-picture']} data-testid="coworker-details-profile-pic">
                    <ProfilePicture imageSource={imgSrc ?? null} size="large" />
                </div>
                <div className={classes.role}>
                    <Text data-testid="coworker-details-legal-full-name" textStyle="Heading.S">{coworker?.legalFullName}</Text>
                    <Text data-testid="coworker-details-job-title" textStyle="Body.M">{coworker?.jobTitle ?? `${t('NO')} ${t('JOB_TITLE')}`}</Text>
                    <Text data-testid="coworker-details-office-location" textStyle="LabelRegular.M">{coworker?.personalAreaName}</Text>
                </div>
            </div>
            <ListView id="coworker-details-content" data-testid="coworker-details-list-view">
                {!isActive && (
                    <ListViewItem
                        data-testid="coworker-details-deactivated"
                        inset
                        emphasised
                        description={<DeactivatedCoworker t={t} />}
                        size="small"
                        showDivider
                    />
                )}
                <ListViewItem
                    data-testid="coworker-details-id-number"
                    inset
                    emphasised
                    title={t('COWORKER_ID_NUMBER')}
                    description={<CoworkerData data={coworker.isDummy ? `${t('NO')} ${t('COWORKER_ID_NUMBER')}` : coworker.personId} />}
                    size="small"
                    showDivider
                />
                <ListViewItem
                    data-testid="coworker-details-business-function"
                    inset
                    emphasised
                    title={t('BUSINESS_FUNCTION')}
                    description={<CoworkerData data={coworker.divisionName ?? `${t('NO')} ${t('BUSINESS_FUNCTION')}`} />}
                    size="small"
                    showDivider
                />
                <ListViewItem
                    data-testid="coworker-details-department"
                    inset
                    emphasised
                    title={t('DEPARTMENT')}
                    description={<CoworkerData data={coworker.departmentName ?? `${t('NO')} ${t('DEPARTMENT')}`} />}
                    size="small"
                    showDivider
                />
                <ListViewItem
                    data-testid="coworker-details-cost-centre"
                    inset
                    emphasised
                    title={t('COST_CENTRE')}
                    description={<CoworkerCostCentre keyPrefix={coworker.personId} costCentres={costCentres} />}
                    size="small"
                    showDivider
                />
                <ListViewItem
                    data-testid="coworker-details-contract-details"
                    inset
                    emphasised
                    title={t('CONTRACT_DETAILS')}
                    description={`
                            ${contractTypes.get(coworker.contractType) || t('N/A')}, ${t(coworker.employmentType)},
                            ${coworker.contractRange ? getContractHoursRange(coworker as Coworker) : coworker.hoursPerWeek} ${t('HRS_PER_WEEK')}`}
                    size="small"
                    showDivider
                />
                <ListViewItem
                    data-testid="coworker-details-contract-period"
                    inset
                    emphasised
                    title={t('CONTRACT_PERIOD')}
                    description={`${formatDate(coworker.contractStartDate) ?? t('N/A')} - ${formatDate(coworker.contractEndDate) ?? t('N/A')}`}
                    size="small"
                    showDivider
                />
                {coworker.contributions.length > 0
                    && (
                    <ListViewItem
                        data-testid="coworker-details-contributions"
                        inset
                        emphasised
                        description={<CoworkerContribution title={t('CONTRIBUTIONS')} contributions={coworker.contributions} />}
                        size="small"
                        showDivider
                    />
                    )}
                {access?.features?.modulation && (
                    <ListViewItem
                        data-testid="coworker-details-modulation"
                        inset
                        emphasised
                        title={t('MODULATION')}
                        description={<CoworkerData data={(coworker as Coworker).isModulated ? t('YES_SHORT') : t('NO')} />}
                        size="small"
                        showDivider
                    />
                )}
            </ListView>
            {(coworker as Coworker).futureData?.map((futureCoworker, index) => (
                <CoworkerFutureData key={`coworker-future-data-${index + 1}`} futureData={futureCoworker} />
            ))}
            {portalDiv
                && createPortal(
                    <CoworkerDetailsEditModal
                        coworker={coworker}
                        isOpen={editCoworkerSheet}
                        setIsOpen={handleEditCoworkerSheet}
                    />,
                    portalDiv,
                )}
        </div>
    );
};

export default CoworkerDetails;
